/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import './style/loginForm.scss';
import {ChangeKeyFunction, Loader, preventDefault} from '@dvrd/dvr-controls';

import {CredentialResponse, GoogleLogin} from '@react-oauth/google';

import React, {
    FocusEventHandler,
    KeyboardEventHandler,
    MouseEventHandler,
    useLayoutEffect,
    useRef,
    useState
} from 'react';

import {FORGOT_PASSWORD_ROUTE} from '../../../util/constants';

import {LoginErrors} from '../../../util/interfaces';

import {BluemateButton, BluemateInput, BluematePasswordInput} from '../../controls';

import {useTranslation} from 'react-i18next';

export interface LoginFormProps {
    onSubmit: MouseEventHandler & KeyboardEventHandler;
    onChange: ChangeKeyFunction<string>;
    onFocusField: (key: keyof LoginErrors) => FocusEventHandler;
    onGoogleLogin: (response: CredentialResponse) => void;
    onGoogleError: () => void;
    onClickGoogle: MouseEventHandler;
    onClickReset?: MouseEventHandler;
    email: string;
    password: string;
    loading: boolean;
    errors: LoginErrors;
}

// The credentials form shared by the login page and the MCP connect flow: the
// fields, error copy and the submit/Google buttons — no page chrome, no
// register link, purely prop-driven so it renders in any container.
export default function LoginForm(props: LoginFormProps) {
    const {
        onGoogleLogin,
        loading,
        onClickGoogle,
        onClickReset,
        errors,
        onChange,
        onFocusField,
        onSubmit,
        password,
        email,
        onGoogleError
    } = props;
    const {t} = useTranslation();
    const formRef = useRef<HTMLFormElement>(null);
    // The Google button renders inside a fixed-pixel-width iframe: it cannot
    // shrink via CSS and only accepts an integer width in [200, 400]. Match it
    // to the form's real rendered width so it lines up with the full-width
    // "Log in" button at every screen size, not just at coarse breakpoint tiers.
    const [googleButtonWidth, setGoogleButtonWidth] = useState('400px');

    useLayoutEffect(() => {
        const el = formRef.current;
        if (!el) return;
        const update = () => {
            const width = Math.round(el.getBoundingClientRect().width);
            const clamped = Math.max(200, Math.min(400, width));
            setGoogleButtonWidth(`${clamped}px`);
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    function renderGoogle() {
        return (
            <div onClick={onClickGoogle} className='google-button-wrapper'>
                <GoogleLogin
                    // Force a clean remount when the width changes; the library
                    // re-runs renderButton on width change but does not clear the
                    // container first, which would otherwise stack buttons.
                    key={googleButtonWidth}
                    onSuccess={onGoogleLogin}
                    onError={onGoogleError}
                    context='signin'
                    text='signin_with'
                    width={googleButtonWidth}
                />
            </div>
        );
    }

    return (
        <div className='login-form'>
            <form onSubmit={preventDefault} className='form' ref={formRef}>
                <Loader active={loading} />
                <BluemateInput
                    value={email}
                    onChange={onChange('email')}
                    error={errors.email}
                    onFocus={onFocusField('email')}
                    label={t('auth.login.emailLabel', 'E-mailadres *')}
                    inputProps={{
                        placeholder: t('auth.login.emailPlaceholder', 'E-mailadres'),
                        autoComplete: 'username'
                    }}
                    onEnter={onSubmit}
                    autoFocus
                />
                <BluematePasswordInput
                    value={password}
                    onChange={onChange('password')}
                    error={errors.password}
                    onFocus={onFocusField('password')}
                    label={t('auth.login.passwordLabel', 'Wachtwoord *')}
                    inputProps={{
                        placeholder: t('auth.login.passwordPlaceholder', 'Wachtwoord'),
                        autoComplete: 'current-password'
                    }}
                    onEnter={onSubmit}
                />
                {/* The credentials copy is shown ONLY when the backend actually
                    rejected the login. This block used to render it for every
                    general error, so a request that was blocked on its way (a
                    Cloudflare challenge inside the installed app) told the user
                    their correct password was wrong — and hid the real reason. */}
                {!!errors.general && (
                    <div className='general-error'>
                        <p>
                            {errors.generalIsCredentials ? (
                                <>
                                    {t(
                                        'auth.login.invalidCredentials',
                                        'Ongeldige combinatie van e-mailadres en wachtwoord.'
                                    )}
                                    <br />
                                    <br />
                                    {t(
                                        'auth.login.checkEmailQuestion',
                                        'Weet je zeker dat dit wel het juiste e-mailadres is?'
                                    )}
                                    <br />
                                    {t(
                                        'auth.login.checkEmailNote',
                                        '90% van de tijd vergissen gebruikers zich in het e-mailadres.'
                                    )}
                                </>
                            ) : (
                                errors.general
                            )}
                        </p>
                    </div>
                )}
                {!!onClickReset && (
                    <a className='reset-link' href={FORGOT_PASSWORD_ROUTE} onClick={onClickReset}>
                        {t('auth.login.forgotPassword', 'Wachtwoord vergeten?')}
                    </a>
                )}
            </form>
            <div className='btns-container'>
                <BluemateButton
                    onClick={onSubmit}
                    label={t('auth.login.submit', 'Log in')}
                    className='submit-btn'
                    disabled={loading}
                />
                {renderGoogle()}
            </div>
        </div>
    );
}
